import React, { useState, useEffect } from 'react'
import { dbOperations } from '../../utils/supabase'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  StarIcon,
  CubeIcon,
  XMarkIcon,
  GiftIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

const PackagesPage = () => {
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(true)
  const [showItemsModal, setShowItemsModal] = useState(false)
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [packageItems, setPackageItems] = useState([])

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const packagesResponse = await dbOperations.packages.getAll()
      setPackages(packagesResponse.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadPackageItems = async (packageId) => {
    try {
      const { data: items } = await dbOperations.packageItems.getByPackageId(packageId)
      setPackageItems(items || [])
    } catch (error) {
      console.error('Error loading package items:', error)
      setPackageItems([])
    }
  }

  const handleDelete = async (packageItem) => {
    if (!window.confirm(`"${packageItem.name}" paketini silmek istediğinizden emin misiniz?`)) {
      return
    }

    try {
      await dbOperations.packages.delete(packageItem.id)
      await loadData()
      alert('Paket silindi!')
    } catch (error) {
      console.error('Error deleting package:', error)
      alert('Paket silinirken hata oluştu!')
    }
  }

  const toggleFeatured = async (packageItem) => {
    try {
      await dbOperations.packages.update(packageItem.id, {
        is_featured: !packageItem.is_featured
      })
      await loadData()
    } catch (error) {
      console.error('Error updating featured status:', error)
      alert('Öne çıkarma durumu güncellenirken hata oluştu!')
    }
  }

  const openItemsModal = async (packageItem) => {
    setSelectedPackage(packageItem)
    await loadPackageItems(packageItem.id)
    setShowItemsModal(true)
  }

  const closeItemsModal = () => {
    setShowItemsModal(false)
    setSelectedPackage(null)
    setPackageItems([])
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount)
  }

  const getItemDisplayName = (item) => {
    if (item.item_type === 'product' && item.products) {
      return item.products.name
    } else if (item.item_type === 'product_set' && item.product_sets) {
      return item.product_sets.name
    }
    return 'Bilinmeyen öğe'
  }

  const getItemPrice = (item) => {
    if (item.item_type === 'product' && item.products) {
      return item.products.price
    } else if (item.item_type === 'product_set' && item.product_sets) {
      return item.product_sets.set_price
    }
    return 0
  }

  const getItemType = (item) => {
    return item.item_type === 'product' ? '🛋️ Ürün' : '📦 Takım'
  }

  if (loading && packages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🎁 Paketler</h1>
          <p className="mt-1 text-sm text-gray-600">
            Ürün ve takım paketlerini yönetin (düğün paketi, ofis paketi vs.)
          </p>
        </div>
        <button
          onClick={() => window.location.href = '/admin/packages/add'}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Yeni Paket
        </button>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((packageItem) => (
          <div key={packageItem.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            {/* Package Image */}
            <div className="h-48 bg-gray-200 relative">
              {packageItem.image_url ? (
                <img
                  src={packageItem.image_url}
                  alt={packageItem.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <GiftIcon className="h-16 w-16 text-gray-400" />
                </div>
              )}
              
              {/* Featured Badge */}
              {packageItem.is_featured && (
                <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                  ⭐ Öne Çıkan
                </div>
              )}
              
              {/* Status Badge */}
              <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
                packageItem.is_active 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {packageItem.is_active ? 'Aktif' : 'Pasif'}
              </div>
            </div>

            {/* Package Info */}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{packageItem.name}</h3>
              {packageItem.category && (
                <p className="text-sm text-gray-500 mb-2">{packageItem.category}</p>
              )}
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {packageItem.description || 'Açıklama bulunmuyor'}
              </p>
              
              {/* Price Info */}
              <div className="mb-4">
                <p className="text-xl font-bold text-indigo-600">
                  {formatCurrency(packageItem.package_price)}
                </p>
                {packageItem.discount_amount > 0 && (
                  <p className="text-sm text-green-600">
                    {formatCurrency(packageItem.discount_amount)} indirim
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => openItemsModal(packageItem)}
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50"
                    title="İçeriği Görüntüle"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => window.location.href = `/admin/packages/edit/${packageItem.id}`}
                    className="p-2 text-gray-400 hover:text-green-600 rounded-full hover:bg-green-50"
                    title="Düzenle"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(packageItem)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50"
                    title="Sil"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                <button
                  onClick={() => toggleFeatured(packageItem)}
                  className={`p-2 rounded-full ${
                    packageItem.is_featured
                      ? 'text-yellow-600 hover:bg-yellow-50'
                      : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                  }`}
                  title={packageItem.is_featured ? 'Öne Çıkarmayı Kaldır' : 'Öne Çıkar'}
                >
                  {packageItem.is_featured ? (
                    <StarIconSolid className="h-5 w-5" />
                  ) : (
                    <StarIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {packages.length === 0 && (
        <div className="text-center py-12">
          <GiftIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Paket bulunamadı</h3>
          <p className="mt-1 text-sm text-gray-500">
            İlk paketinizi oluşturmak için başlayın.
          </p>
          <div className="mt-6">
            <button
              onClick={() => window.location.href = '/admin/packages/add'}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Yeni Paket
            </button>
          </div>
        </div>
      )}

      {/* Package Items Modal */}
      {showItemsModal && selectedPackage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                📦 {selectedPackage.name} - İçerik
              </h3>
              <button
                onClick={closeItemsModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {packageItems.length > 0 ? (
                packageItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-gray-500">
                          {getItemType(item)}
                        </span>
                        <h4 className="text-sm font-medium text-gray-900">
                          {getItemDisplayName(item)}
                        </h4>
                      </div>
                      <p className="text-sm text-gray-500">
                        Adet: {item.quantity} × {formatCurrency(getItemPrice(item))}
                      </p>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(getItemPrice(item) * item.quantity)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CubeIcon className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Bu pakette henüz öğe bulunmuyor</p>
                </div>
              )}
            </div>

            {packageItems.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-gray-900">
                    Toplam İçerik Değeri:
                  </span>
                  <span className="text-lg font-bold text-indigo-600">
                    {formatCurrency(packageItems.reduce((total, item) => 
                      total + (getItemPrice(item) * item.quantity), 0
                    ))}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-lg font-medium text-gray-900">
                    Paket Fiyatı:
                  </span>
                  <span className="text-lg font-bold text-green-600">
                    {formatCurrency(selectedPackage.package_price)}
                  </span>
                </div>
                {selectedPackage.discount_amount > 0 && (
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-gray-600">
                      Paket İndirimi:
                    </span>
                    <span className="text-sm font-medium text-red-600">
                      -{formatCurrency(selectedPackage.discount_amount)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PackagesPage 